import type { Order, OrderAttachment, OrderBucket, ServiceCode } from '@/common/order.common'
import type { BankService, OrderSettings } from '@/common/settings.common'
import { API_BASE_URL } from '@/config'

// conversion ratios based on backend rules
const HOURS_PER_UNIT: Record<ServiceCode, number> = {
  TOJ: 2.5,
  CONSULTING: 1,
  TRAINING: 8,
  TOJ_CUSTOMER: 2.5,
  PILOT: 1,
}

const DEFAULT_HOURS_PER_UNIT: Record<string, number> = {
  TOJ: 2.5,
  CONSULTING: 1,
  TRAINING: 8,
  TOJ_CUSTOMER: 2.5,
  PILOT: 1,
}

export function getHoursPerUnit(serviceCode: string, fromCatalog?: number) {
  return fromCatalog ?? DEFAULT_HOURS_PER_UNIT[serviceCode] ?? 1
}

export type HoursMap = Record<ServiceCode, number>

export const EMPTY_HOURS: HoursMap = {
  TOJ: 0,
  CONSULTING: 0,
  TRAINING: 0,
  PILOT: 0,
}

export function hoursToUnits(serviceCode: string, hours: number): number {
  const hpu = HOURS_PER_UNIT[serviceCode] ?? 1
  return Number((hours / hpu).toFixed(2))
}
export function unitsToHours(serviceCode: string, units: number): number {
  const hpu = HOURS_PER_UNIT[serviceCode] ?? 1
  return Number((units * hpu).toFixed(2))
}

export function downloadOrderAttachmentUrl(orderId: number, attachmentId: number) {
  return `${API_BASE_URL}/orders/${orderId}/attachments/${attachmentId}/download`
}

export function mapAttachmentToFrontend(orderId: number, a: OrderAttachment) {
  return {
    id: a.id,
    name: a.filename,
    url: downloadOrderAttachmentUrl(orderId, a.id),
  }
}

export function mapApiStatusToUiStatus(apiStatus: string) {
  switch (apiStatus) {
    case 'SUBMITTED':
      return 'ABGESENDET'
    case 'APPROVED':
      return 'FREIGEGEBEN'
    case 'REJECTED':
      return 'ABGELEHNT'
    case 'CORRECTED':
      return 'KORREKTUR'
    case 'COMPLETED':
      return 'KORREKTUR_AKZEPTIERT'
    case 'DELETED':
      return 'KORREKTUR_ABGELEHNT'
    default:
      return 'IN_BEARBEITUNG'
  }
}

export function sumOrderBuckets(buckets: OrderBucket[]): HoursMap {
  return buckets.reduce(
    (acc, b) => {
      acc[b.serviceType] = (acc[b.serviceType] ?? 0) + (b.plannedHours ?? 0)

      return acc
    },
    { ...EMPTY_HOURS }
  )
}

export function addOrderHours(a: HoursMap, b: HoursMap): HoursMap {
  return {
    TOJ: a.TOJ + b.TOJ,
    CONSULTING: a.CONSULTING + b.CONSULTING,
    TRAINING: a.TRAINING + b.TRAINING,
    PILOT: (a.PILOT ?? 0) + (b.PILOT ?? 0),
  }
}

export function subtractOrderHours(a: HoursMap, b: HoursMap): HoursMap {
  return {
    TOJ: a.TOJ - b.TOJ,
    CONSULTING: a.CONSULTING - b.CONSULTING,
    TRAINING: a.TRAINING - b.TRAINING,
    PILOT: (a.PILOT ?? 0) - (b.PILOT ?? 0),
  }
}

export function extractOrderYear(order?: Order | null): number | null {
  if (!order) return null
  return new Date(order.startTime).getFullYear()
}

const REQUIRED_SERVICES = ['TOJ', 'CONSULTING', 'TRAINING']

export function hasCompletedCapacitySettings(capacities: OrderSettings[]): boolean {
  if (!capacities || capacities.length === 0) return false

  return capacities.some(
    c => REQUIRED_SERVICES.includes(c.service?.code) && Number(c.capacity) > 0
  )
}

export function hasCompletedUnionSettings(services: BankService[]): boolean {
  return REQUIRED_SERVICES.every(code =>
    services.some(b => {
      if (b.serviceCode !== code) return false

      const price = b.price
      return price !== null && price !== undefined && !Number.isNaN(price)
    })
  )
}

export function resolveOrderCustomerName(
  order: Order,
  unionAssociatedEntities: {
    id: number
    name: string
    name2?: string | null
    name3?: string | null
  }[]
) {
  const customerId = Number((order as any).customerId ?? 0)

  // New orders: customerId points to associated entity
  if (customerId > 0) {
    const hit = unionAssociatedEntities.find(e => Number(e.id) === customerId)
    if (hit) return [hit.name, hit.name2, hit.name3].filter(Boolean).join(' – ')
    return order.bankName ?? '—'
  }

  // Old orders: customerId=0, bankName already is the real customer
  return order.bankName ?? '—'
}

export function formatBankNameParts(...parts: Array<string | null | undefined>) {
  return parts.filter(Boolean).join(' – ')
}

export function formatBankLabel(
  name?: string | null,
  name2?: string | null,
  name3?: string | null
) {
  return [name, name2, name3].filter(Boolean).join(' – ')
}
